import { z } from 'zod';
import { countryCode } from './schemas.ts';
import {
  isKnownCountry,
  isValidCnpj,
  isValidCpf,
  normalizeTaxId,
  type SituationInput,
} from './partners.ts';

/**
 * Dados bancários e condições de pagamento dos parceiros (C3a.2, ADR 0016): validações, entradas
 * da API e a aptidão para pagamento, usadas pela API, pelas telas e, depois, por contas a pagar.
 */

export const BANK_ACCOUNT_KINDS = ['br_account', 'pix', 'iban', 'swift'] as const;
export type BankAccountKind = (typeof BANK_ACCOUNT_KINDS)[number];

export const BANK_ACCOUNT_STATUSES = [
  'pending',
  'active',
  'rejected',
  'superseded',
  'inactive',
] as const;
export type BankAccountStatus = (typeof BANK_ACCOUNT_STATUSES)[number];

export const PIX_KEY_TYPES = ['cpf', 'cnpj', 'email', 'phone', 'random'] as const;
export type PixKeyType = (typeof PIX_KEY_TYPES)[number];

export const BANK_ACCOUNT_TYPES = ['checking', 'savings', 'payment'] as const;

export const PAYMENT_METHODS = ['bank_transfer', 'pix', 'boleto', 'card', 'other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
/**
 * Vendedor de marketplace (Compras, fluxo D) recebe pela plataforma: só cartão ou "outra" forma.
 * O banco também confere (migração 0203).
 */
export const MARKETPLACE_PAYMENT_METHODS: readonly PaymentMethod[] = ['card', 'other'];

/** IBAN: país, dígitos de controle e conta, conferidos pelo resto da divisão por 97. */
export function isValidIban(value: string): boolean {
  const iban = value.replace(/\s+/g, '').toUpperCase();
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  const moved = iban.slice(4) + iban.slice(0, 4);
  let rest = 0;
  for (const ch of moved) {
    const n = ch >= 'A' ? ch.charCodeAt(0) - 55 : Number(ch);
    rest = Number(`${rest}${n}`) % 97;
  }
  return rest === 1;
}

/** Chave PIX no formato canônico, ou null se não for válida para o tipo. */
export function normalizePixKey(type: PixKeyType, value: string): string | null {
  const v = value.trim();
  switch (type) {
    case 'cpf': {
      const d = normalizeTaxId(v);
      return isValidCpf(d) ? d : null;
    }
    case 'cnpj': {
      const d = normalizeTaxId(v);
      return isValidCnpj(d) ? d : null;
    }
    case 'email': {
      const e = v.toLowerCase();
      return e.length <= 77 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e) ? e : null;
    }
    case 'phone': {
      const p = `+${v.replace(/[^0-9]/g, '')}`;
      return /^\+[1-9][0-9]{7,14}$/.test(p) ? p : null;
    }
    case 'random': {
      const r = v.toLowerCase();
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(r) ? r : null;
    }
  }
}

// ----- Contas no exterior (Entrega 3) -----

/** Países que usam IBAN (registro SWIFT). O Brasil fica de fora: lá vale a conta no Brasil. */
export const IBAN_COUNTRIES = new Set(
  (
    'AD AE AL AT AZ BA BE BG BH BI BY CH CR CY CZ DE DJ DK DO EE EG ES FI FK FO FR GB GE GI GL GR ' +
    'GT HN HR HU IE IL IQ IS IT JO KW KZ LB LC LI LT LU LV LY MC MD ME MK MN MR MT MU NI NL NO OM ' +
    'PK PL PS PT QA RO RS RU SA SC SD SE SI SK SM SO ST SV TL TN TR UA VA VG XK YE'
  ).split(' '),
);

export const LOCAL_CODE_TYPES = ['aba', 'sort_code', 'bsb', 'ifsc', 'ca_routing'] as const;
export type LocalCodeType = (typeof LOCAL_CODE_TYPES)[number];

/** Código local do banco exigido por país, quando a conta não é IBAN. */
export const LOCAL_CODE_BY_COUNTRY: Record<string, LocalCodeType> = {
  US: 'aba',
  GB: 'sort_code',
  AU: 'bsb',
  IN: 'ifsc',
  CA: 'ca_routing',
};

/** ABA (EUA): 9 dígitos com dígito verificador (pesos 3, 7, 1). */
export function isValidAba(value: string): boolean {
  if (!/^[0-9]{9}$/.test(value)) return false;
  const w = [3, 7, 1, 3, 7, 1, 3, 7, 1];
  return [...value].reduce((sum, d, i) => sum + Number(d) * w[i]!, 0) % 10 === 0;
}

/** CLABE (México): 18 dígitos com dígito verificador (pesos 3, 7, 1). */
export function isValidClabe(value: string): boolean {
  if (!/^[0-9]{18}$/.test(value)) return false;
  const w = [3, 7, 1];
  const sum = [...value.slice(0, 17)].reduce(
    (acc, d, i) => acc + ((Number(d) * w[i % 3]!) % 10),
    0,
  );
  return (10 - (sum % 10)) % 10 === Number(value[17]);
}

/** Código local no formato canônico (só letras e números), ou null se não for válido. */
export function normalizeLocalCode(type: LocalCodeType, value: string): string | null {
  const v = value.replace(/[\s.-]+/g, '').toUpperCase();
  switch (type) {
    case 'aba':
      return isValidAba(v) ? v : null;
    case 'sort_code':
    case 'bsb':
      return /^[0-9]{6}$/.test(v) ? v : null;
    case 'ifsc':
      return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(v) ? v : null;
    case 'ca_routing':
      // Agência (transit, 5 dígitos) e instituição (3 dígitos).
      return /^[0-9]{8}$/.test(v) ? v : null;
  }
}

// ----- Entradas -----

const text = (max: number, min = 1) => z.string().trim().min(min).max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const currency = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{3}$/, 'Moeda inválida');
const bankCountry = countryCode.refine(isKnownCountry, 'País inválido');
const holderTaxId = optionalText(40).transform((v) => (v == null ? null : normalizeTaxId(v)));
const upperNoSpaces = (re: RegExp, message: string) =>
  z
    .string()
    .transform((v) => v.replace(/[\s.]+/g, '').toUpperCase())
    .pipe(z.string().regex(re, message));

const common = {
  holderName: text(200),
  holderTaxId,
  currency,
  /** Por que a conta está sendo incluída ou alterada (vai para o histórico). */
  changeReason: text(1000, 3),
};

/** Banco pela lista do Banco Central (ISPB) ou, sem a lista carregada, pelo código. */
const bankIspb = z
  .string()
  .trim()
  .regex(/^[0-9]{8}$/, 'Banco inválido')
  .nullable()
  .optional();

const brAccountFields = {
  bankIspb,
  bankCode: z
    .string()
    .trim()
    .regex(/^[0-9]{3}$/, 'Código do banco com 3 dígitos'),
  bankName: optionalText(200),
  branch: upperNoSpaces(/^[0-9]{1,5}(-[0-9A-Z])?$/, 'Agência inválida'),
  accountNumber: upperNoSpaces(/^[0-9]{1,20}-?[0-9A-Z]$/, 'Conta inválida'),
  accountType: z.enum(BANK_ACCOUNT_TYPES),
};

const swiftBic = upperNoSpaces(/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/, 'SWIFT/BIC inválido');

/**
 * Conta no exterior (Entrega 3): SWIFT do banco, nome e endereço do banco e, se houver, o banco
 * intermediário (SWIFT, nome e a conta do banco do fornecedor no intermediário).
 */
const intlFields = {
  swiftBic,
  bankName: text(200),
  bankAddress: text(300),
  intermediary: z
    .object({
      swiftBic,
      name: text(200),
      account: upperNoSpaces(/^[A-Z0-9]{1,34}$/, 'Conta no intermediário inválida')
        .nullable()
        .optional(),
    })
    .nullable()
    .optional(),
};

export const bankAccountInput = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('br_account'),
    ...common,
    ...brAccountFields,
  }),
  z.object({
    kind: z.literal('pix'),
    ...common,
    pixKeyType: z.enum(PIX_KEY_TYPES),
    pixKey: text(100),
  }),
  z.object({
    kind: z.literal('iban'),
    ...common,
    ...intlFields,
    iban: upperNoSpaces(/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/, 'IBAN inválido')
      .refine(isValidIban, 'IBAN inválido')
      .refine((v) => !v.startsWith('BR'), 'Conta no Brasil: use o tipo Conta no Brasil'),
  }),
  z
    .object({
      kind: z.literal('swift'),
      ...common,
      ...intlFields,
      country: bankCountry.refine((c) => c !== 'BR', 'Conta no Brasil: use o tipo Conta no Brasil'),
      accountNumber: upperNoSpaces(/^[A-Z0-9]{1,34}$/, 'Conta inválida'),
      localCode: optionalText(20),
    })
    .superRefine((v, ctx) => {
      // Países de IBAN (fora o Reino Unido, que também aceita sort code e conta): IBAN.
      if (IBAN_COUNTRIES.has(v.country) && v.country !== 'GB') {
        ctx.addIssue({ code: 'custom', path: ['country'], message: 'iban_required' });
      }
      const type = LOCAL_CODE_BY_COUNTRY[v.country];
      if (type && !(v.localCode && normalizeLocalCode(type, v.localCode))) {
        ctx.addIssue({ code: 'custom', path: ['localCode'], message: `local_code_${type}` });
      }
      if (v.country === 'MX' && !isValidClabe(v.accountNumber)) {
        ctx.addIssue({ code: 'custom', path: ['accountNumber'], message: 'clabe_invalid' });
      }
    }),
]);
export type BankAccountInput = z.infer<typeof bankAccountInput>;

/**
 * Conta no Brasil numa tela só (Entrega 2, D3): conta e, se quiser, a chave PIX. Por baixo viram
 * duas contas, cada uma com a sua confirmação.
 */
export const brazilBankInput = z.object({
  ...common,
  ...brAccountFields,
  pix: z
    .object({ pixKeyType: z.enum(PIX_KEY_TYPES), pixKey: text(100) })
    .nullable()
    .optional(),
});
export type BrazilBankInput = z.infer<typeof brazilBankInput>;

/**
 * Titular da conta com documento diferente do fornecedor (auditoria de 27/10/2026): não impede,
 * só avisa quem cadastra e quem confirma. Sem um dos dois documentos, não há o que comparar.
 */
export function bankHolderDiffers(
  partnerTaxId: string | null | undefined,
  holderTaxId: string | null | undefined,
): boolean {
  if (!partnerTaxId || !holderTaxId) return false;
  const holder = normalizeTaxId(holderTaxId);
  return holder !== '' && holder !== normalizeTaxId(partnerTaxId);
}

/**
 * O que a conta guarda cifrado (número, chave PIX ou IBAN), o país e os últimos caracteres para
 * mostrar mascarado. Erro de chave PIX volta como `null`.
 */
export function bankAccountSecret(
  input: BankAccountInput,
): { secret: string; last4: string; country: string } | null {
  let secret: string | null;
  let country = 'BR';
  switch (input.kind) {
    case 'br_account':
      secret = input.accountNumber;
      break;
    case 'pix':
      secret = normalizePixKey(input.pixKeyType, input.pixKey);
      break;
    case 'iban':
      secret = input.iban;
      country = input.iban.slice(0, 2);
      break;
    case 'swift':
      secret = input.accountNumber;
      country = input.country;
      break;
  }
  if (!secret) return null;
  return { secret, last4: secret.slice(-4), country };
}

export const paymentTermsInput = z.object({
  method: z.enum(PAYMENT_METHODS),
  netDays: z.number().int().min(0).max(365),
  installments: z.number().int().min(1).max(60).default(1),
  /** Dia fixo de vencimento no mês, quando aplicável. */
  dueDay: z.number().int().min(1).max(31).nullable().optional(),
  notes: optionalText(1000),
});
export type PaymentTermsInput = z.infer<typeof paymentTermsInput>;

/** Confirmar ou recusar uma conta: sempre com um comentário. */
export const bankDecisionInput = z.object({ comment: text(1000) });
export const bankInactivateInput = z.object({ reason: text(1000, 3) });
/** Ver o número completo: o motivo fica registrado. */
export const bankRevealInput = z.object({ purpose: text(300, 3) });

// ----- Aptidão para pagamento -----

export type PaymentBlocker = 'not_linked' | 'restricted' | 'no_confirmed_account';

/** Formas de pagamento que não usam a conta do parceiro (o boleto traz os dados de quem recebe). */
const METHODS_WITHOUT_ACCOUNT: readonly PaymentMethod[] = ['boleto', 'card'];

/**
 * Pode pagar o parceiro nesta empresa? Independe da aptidão para contratar (ADR 0014): bloqueio
 * comercial, homologação pendente e inativação não impedem pagar o que já foi contratado. Contam
 * os bloqueios que impedem pagamentos (no grupo ou na empresa) e, quando a forma de pagamento usa
 * a conta do parceiro, ter ao menos uma conta confirmada.
 */
export function paymentAptitude(
  p: Pick<SituationInput, 'links' | 'restrictions'>,
  legalEntityId: string,
  opts: {
    hasConfirmedAccount: boolean;
    method?: PaymentMethod | null;
    marketplaceSeller?: boolean;
  },
): { apt: boolean; blockers: PaymentBlocker[]; restrictionIds: string[] } {
  const blockers: PaymentBlocker[] = [];
  if (!p.links.some((l) => l.legalEntityId === legalEntityId)) blockers.push('not_linked');
  const restrictionIds = p.restrictions
    .filter(
      (r) => r.blocksPayments && (r.legalEntityId == null || r.legalEntityId === legalEntityId),
    )
    .map((r) => r.id);
  if (restrictionIds.length > 0) blockers.push('restricted');
  // O vendedor de marketplace não tem conta (migração 0202): recebe pela plataforma.
  const needsAccount =
    !opts.marketplaceSeller && (!opts.method || !METHODS_WITHOUT_ACCOUNT.includes(opts.method));
  if (needsAccount && !opts.hasConfirmedAccount) blockers.push('no_confirmed_account');
  return { apt: blockers.length === 0, blockers, restrictionIds };
}
